import { createFileRoute, useNavigate } from '@tanstack/react-router'
import type { PublicInvestorData } from '@workspace/api-backend'
import { Badge } from '@workspace/ui/components/badge'
import { Button } from '@workspace/ui/components/button'
import { LabelValue } from '@workspace/ui/components/label-value'
import { ProfileHeader } from '@workspace/ui/components/profile-header'
import { ProfileSection } from '@workspace/ui/components/profile-section'
import { StatusCard } from '@workspace/ui/components/status-card'
import { Globe, LogIn, MapPin, Mic, PauseCircle, Sparkles } from 'lucide-react'
import { AppLayout } from '@/components/app-layout'
import { QueryGuard } from '@/components/query-guard'
import { PitchPrepare } from '../features/pitch/components/pitch-prepare'
import { usePublicInvestorQuery } from '../hooks/api'
import { useIsFounder, useIsInvestor } from '../hooks/use-user-type'
import { setAuthRedirect, useSession } from '../lib/auth'

export const Route = createFileRoute('/$slug')({
  component: SlugPage,
})

function SlugPage() {
  return (
    <AppLayout>
      <InvestorPitchPage />
    </AppLayout>
  )
}

function InvestorPitchPage() {
  const { slug } = Route.useParams()
  const investorQuery = usePublicInvestorQuery(slug)

  return (
    <QueryGuard query={investorQuery} errorMessage="Investor not found">
      {(investor) => <InvestorPitchContent investor={investor} />}
    </QueryGuard>
  )
}

function InvestorPitchContent({ investor }: { investor: PublicInvestorData }) {
  const navigate = useNavigate()
  const { data: session } = useSession()
  const isFounder = useIsFounder()
  const isInvestor = useIsInvestor()
  const isLoggedIn = !!session

  return (
    <>
      <ProfileHeader
        coverImageUrl={investor.coverImageUrl}
        avatarUrl={investor.logoUrl}
        name={investor.name}
        badges={
          <Badge variant="outline-primary" size="md">
            Investor
          </Badge>
        }
      />

      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-6">
          <div className="flex flex-wrap items-center gap-6">
            {investor.websiteUrl && (
              <a
                href={investor.websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline"
              >
                <LabelValue
                  icon={<Globe className="size-4" />}
                  label="Website"
                  value={new URL(investor.websiteUrl).hostname}
                  direction="horizontal"
                />
              </a>
            )}
          </div>
          {investor.investmentGeographies.length > 0 && (
            <div className="flex flex-wrap gap-2 ml-auto">
              {investor.investmentGeographies.map((geo) => (
                <Badge key={geo.value} variant="secondary" size="md" className="gap-1">
                  <MapPin className="size-3" />
                  {geo.label}
                </Badge>
              ))}
            </div>
          )}
        </div>

        {investor.investmentAreas.length > 0 && (
          <ProfileSection title="Investment Focus">
            <div className="flex flex-wrap gap-2">
              {investor.investmentAreas.map((area) => (
                <Badge key={area.value} variant="secondary" size="md">
                  {area.label}
                </Badge>
              ))}
            </div>
          </ProfileSection>
        )}

        <ProfileSection title={`Pitch to ${investor.name}`}>
          {!investor.isAcceptingPitches ? (
            <StatusCard
              variant="info"
              icon={<PauseCircle className="size-8 text-blue-500" />}
              title="Not Accepting Pitches"
              description={`${investor.name} is not currently accepting new pitches. Please check back later.`}
            />
          ) : isLoggedIn && isFounder ? (
            <div className="max-w-2xl mx-auto">
              <PitchPrepare investorId={investor.id} />
            </div>
          ) : isLoggedIn && isInvestor ? (
            <StatusCard
              variant="info"
              title="Pitching is for Founders"
              description="You're signed in as an investor. To pitch to this investor, please sign in with a founder account."
            />
          ) : (
            <PitchCTA
              investorName={investor.name}
              onSignIn={() => {
                setAuthRedirect(window.location.pathname)
                navigate({ to: '/' })
              }}
            />
          )}
        </ProfileSection>
      </div>
    </>
  )
}

interface PitchCTAProps {
  investorName: string
  onSignIn: () => void
}

function PitchCTA({ investorName, onSignIn }: PitchCTAProps) {
  return (
    <div className="bg-card border border-border rounded-xl p-8 text-center space-y-6">
      <div className="space-y-2">
        <h3 className="text-xl font-semibold text-foreground">Want to pitch to {investorName}?</h3>
        <p className="text-muted-foreground max-w-md mx-auto">
          Record your pitch and let AI evaluate it. Top pitches get seen by investors.
        </p>
      </div>

      <ul className="space-y-3 text-sm text-left max-w-sm mx-auto">
        <li className="flex items-start gap-3">
          <Mic className="size-4 mt-0.5 text-primary shrink-0" />
          <span className="text-muted-foreground">15-minute pitch presentation with your deck</span>
        </li>
        <li className="flex items-start gap-3">
          <Sparkles className="size-4 mt-0.5 text-primary shrink-0" />
          <span className="text-muted-foreground">
            Get AI feedback from GPT, Claude, Grok, and Gemini
          </span>
        </li>
      </ul>

      <Button size="lg" onClick={onSignIn} className="gap-2">
        <LogIn className="size-4" />
        Get Started
      </Button>
    </div>
  )
}
